import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send, Loader2, ThumbsUp, ThumbsDown, Reply, MoreVertical, Edit2, Trash2, ChevronRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useAllTopics } from '@/hooks/useAllTopics';
import SEO from '@/components/SEO';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { MentionTextarea } from '@/components/MentionTextarea';
import { parseMentions, extractMentions } from '@/utils/mentionParser';

console.log('CategoryForumPage module loaded');

interface ForumPost {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  parent_post_id: string | null;
  reactions?: {
    like: number;
    dislike: number;
    userReaction?: string | null;
  };
  replies?: ForumPost[];
}

const CategoryForumPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { user } = useAuth();
  const { topics } = useAllTopics();
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [allPosts, setAllPosts] = useState<ForumPost[]>([]); // Flat array of all posts including replies
  const [newPost, setNewPost] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingPosts, setFetchingPosts] = useState(true);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [currentUsername, setCurrentUsername] = useState<string>('');
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  
  console.log('CategoryForumPage rendered', { categoryId, user: user?.id, postsCount: posts.length });
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const topicData = topics.find(topic => topic.id === categoryId);

  useEffect(() => {
    console.log('useEffect for fetchPosts triggered', { categoryId });
    if (categoryId) {
      fetchPosts();
    }
    if (user) {
      fetchCurrentUsername();
    }
  }, [categoryId, user]);

  const fetchCurrentUsername = async () => {
    if (!user) return;
    
    const { data } = await supabase
      .from('public_profile_data')
      .select('username')
      .eq('id', user.id)
      .single();

    if (data?.username) {
      setCurrentUsername(data.username);
    }
  };

  const fetchPosts = async () => {
    console.log('fetchPosts called for category:', categoryId);
    try {
      setFetchingPosts(true);
      
      // Fetch ALL posts for this category (including replies)
      const { data: allPostsData, error } = await supabase
        .from('category_forum_posts')
        .select('*')
        .eq('category_id', categoryId)
        .order('created_at', { ascending: true });

      console.log('All posts fetched:', allPostsData, 'Error:', error);

      if (error) throw error;

      // Fetch reactions for all posts
      const postsWithReactions = await Promise.all(
        (allPostsData || []).map(async (post) => {
          const reactions = await fetchPostReactions(post.id);
          return { ...post, reactions, replies: [] };
        })
      );

      // Build the nested structure
      const topLevelPosts = postsWithReactions.filter(p => !p.parent_post_id);
      const postsMap = new Map(postsWithReactions.map(p => [p.id, { ...p, replies: [] }]));
      
      // Build the tree
      postsWithReactions.forEach(post => {
        if (post.parent_post_id) {
          const parent = postsMap.get(post.parent_post_id);
          if (parent) {
            parent.replies = parent.replies || [];
            parent.replies.push(postsMap.get(post.id)!);
          }
        }
      });

      const finalPosts = topLevelPosts.map(p => postsMap.get(p.id)!);

      console.log('Posts with details:', finalPosts);
      console.log('All posts flat:', postsWithReactions);
      setPosts(finalPosts);
      setAllPosts(postsWithReactions); // Store flat array for easy lookup
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load forum posts');
    } finally {
      setFetchingPosts(false);
    }
  };

  const fetchPostReactions = async (postId: string) => {
    const { data: reactionsData } = await supabase
      .from('category_forum_reactions')
      .select('reaction_type, user_id')
      .eq('post_id', postId);

    const like = reactionsData?.filter(r => r.reaction_type === 'like').length || 0;
    const dislike = reactionsData?.filter(r => r.reaction_type === 'dislike').length || 0;
    const userReaction = user ? reactionsData?.find(r => r.user_id === user.id)?.reaction_type : null;

    return { like, dislike, userReaction };
  };

  const handleSubmitPost = async (e: React.FormEvent, parentPostId: string | null = null) => {
    e.preventDefault();
    
    const content = parentPostId ? editingPost === parentPostId ? editContent : newPost : newPost;
    
    if (!content.trim()) {
      toast.error('Please enter some content');
      return;
    }
    
    if (!user) {
      toast.error('Please log in to post');
      return;
    }

    try {
      setLoading(true);

      const { data: profileData, error: profileError } = await supabase
        .from('public_profile_data')
        .select('username, avatar_url')
        .eq('id', user.id)
        .single();

      if (editingPost) {
        const { error } = await supabase
          .from('category_forum_posts')
          .update({ content: content.trim() })
          .eq('id', editingPost);

        if (error) throw error;
        setEditingPost(null);
        setEditContent('');
        toast.success('Post updated successfully!');
      } else {
        const { data: insertedPost, error } = await supabase
          .from('category_forum_posts')
          .insert([{
            category_id: categoryId,
            user_id: user.id,
            content: content.trim(),
            username: profileData?.username || 'Anonymous',
            avatar_url: profileData?.avatar_url || null,
            parent_post_id: parentPostId
          }])
          .select()
          .single();

        if (error) throw error;

        // Extract and handle mentions from the content
        const extractedMentions = extractMentions(content.trim());
        if (extractedMentions.length > 0 && insertedPost) {
          const mentionPromises = extractedMentions.map(async (username) => {
            const { data: mentionedUser } = await supabase
              .from('public_profile_data')
              .select('id')
              .eq('username', username)
              .single();

            if (mentionedUser) {
              await supabase.from('category_forum_mentions').insert({
                post_id: insertedPost.id,
                mentioned_user_id: mentionedUser.id,
                mentioned_username: username
              });
            }
          });
          await Promise.all(mentionPromises);
        }

        setNewPost('');
        setReplyingTo(null);
        
        // Only show success for top-level posts, not replies
        if (!parentPostId) {
          toast.success('Post added successfully!');
        }
      }

      await fetchPosts();
    } catch (error) {
      console.error('Error posting:', error);
      toast.error('Failed to post');
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (postId: string, reactionType: 'like' | 'dislike') => {
    if (!user) {
      toast.error('Please log in to react');
      return;
    }

    try {
      const post = allPosts.find(p => p.id === postId);
      
      if (!post) {
        return;
      }
      
      const currentReaction = post?.reactions?.userReaction;

      // If user already has this reaction, remove it
      if (currentReaction === reactionType) {
        const { error } = await supabase
          .from('category_forum_reactions')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id);
        
        if (error) {
          toast.error(`Failed to remove reaction: ${error.message}`);
          return;
        }
      } else {
        // Delete existing reaction if switching
        await supabase
          .from('category_forum_reactions')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id);

        // Add new reaction
        const { error } = await supabase
          .from('category_forum_reactions')
          .insert({
            post_id: postId,
            user_id: user.id,
            reaction_type: reactionType
          });
        
        if (error) {
          toast.error(`Failed to add reaction: ${error.message}`);
          return;
        }
      }

      await fetchPosts();
    } catch (error: any) {
      toast.error(`Error: ${error.message || 'Failed to update reaction'}`);
    }
  };

  const handleDelete = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('category_forum_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const startEditing = (post: ForumPost) => {
    setEditingPost(post.id);
    setEditContent(post.content);
  };

  const renderPost = (post: ForumPost, depth: number = 0) => {
    const isEditing = editingPost === post.id;
    const isOwnPost = user?.id === post.user_id;
    const replies = post.replies || [];
    const isExpanded = expandedReplies.has(post.id);
    const isReply = depth > 0;
    
    // Only indent first-level replies (depth 1), not deeper ones
    const indentClass = depth === 1 ? 'ml-8 sm:ml-12' : '';

    const mostRecentReply = replies.length > 0 
      ? replies.reduce((latest, current) => 
          new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        )
      : null;

    const toggleReplies = () => {
      setExpandedReplies(prev => {
        const newSet = new Set(prev);
        if (newSet.has(post.id)) {
          newSet.delete(post.id);
        } else {
          newSet.add(post.id);
        }
        return newSet;
      });
    };

    return (
      <div 
        key={post.id}
        id={`post-${post.id}`}
        className={`${indentClass} py-3 ${isReply ? '' : 'border-b border-border/40'}`}
      >
        <div className="flex gap-2 sm:gap-3">
          {/* User Avatar with vertical line for replies */}
          <div className="relative flex-shrink-0">
            <Link to={`/user/${post.username}`}>
              <Avatar className="h-8 w-8 sm:h-10 sm:w-10 relative z-10">
                <AvatarImage src={post.avatar_url || ''} className="object-cover" />
                <AvatarFallback className="bg-muted text-xs">
                  {post.username[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </Link>
            {/* Curved line connector - only show for top-level posts with replies */}
            {!isReply && replies.length > 0 && (
              <div className="absolute left-1/2 top-10 sm:top-12 w-8 h-12">
                <div className="absolute left-0 top-0 w-0.5 h-8 bg-border" />
                <div className="absolute left-0 top-8 w-8 h-4 border-l-2 border-b-2 border-border rounded-bl-lg" />
              </div>
            )}
          </div>

          {/* Post Content */}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2 mb-1">
              <div className="flex-1 min-w-0 flex items-baseline gap-1 sm:gap-2 flex-wrap">
                <Link 
                  to={`/user/${post.username}`}
                  className="text-sm font-medium hover:underline"
                >
                  @{post.username}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                </span>
              </div>
              {user && isOwnPost && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background border z-50">
                    <DropdownMenuItem onClick={() => startEditing(post)}>
                      <Edit2 className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(post.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-2 mb-2">
                <MentionTextarea
                  value={editContent}
                  onChange={setEditContent}
                  placeholder="Edit your post..."
                  className="min-h-[60px] text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={(e) => handleSubmitPost(e, post.parent_post_id)}
                    disabled={loading}
                    className="h-8 text-xs"
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingPost(null);
                      setEditContent('');
                    }}
                    className="h-8 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm mb-2 break-words">
                {parseMentions(post.content, currentUsername)}
              </p>
            )}

            {/* Action Buttons */}
            {!isEditing && (
              <div className="flex items-center gap-1 sm:gap-3 flex-wrap">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleReaction(post.id, 'like')}
                  className={`h-7 px-2 text-xs ${post.reactions?.userReaction === 'like' ? 'text-primary' : ''}`}
                >
                  <ThumbsUp className="h-3.5 w-3.5 mr-1" />
                  <span className="text-xs">{post.reactions?.like || 0}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleReaction(post.id, 'dislike')}
                  className={`h-7 px-2 text-xs ${post.reactions?.userReaction === 'dislike' ? 'text-destructive' : ''}`}
                >
                  <ThumbsDown className="h-3.5 w-3.5 mr-1" />
                  <span className="text-xs">{post.reactions?.dislike || 0}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReplyingTo(replyingTo === post.id ? null : post.id)}
                  className="h-7 px-2 text-xs"
                >
                  <Reply className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Reply</span>
                </Button>
              </div>
            )}

            {/* Reply Form */}
            {replyingTo === post.id && !isEditing && (
              <div className="mt-2 space-y-2">
                <MentionTextarea
                  value={newPost}
                  onChange={setNewPost}
                  placeholder={`Reply to ${post.username}... Use @ to mention users`}
                  className="min-h-[60px] text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={(e) => handleSubmitPost(e, post.id)}
                    disabled={loading || !newPost.trim()}
                    size="icon"
                    className="rounded-full h-8 w-8"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setReplyingTo(null);
                      setNewPost('');
                    }}
                    size="sm"
                    className="h-8 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Collapsed replies preview */}
            {replies.length > 0 && mostRecentReply && (
              <div className="mt-3 flex items-start gap-2 sm:gap-3">
                <Link to={`/user/${mostRecentReply.username}`}>
                  <Avatar className="h-6 w-6 flex-shrink-0">
                    <AvatarImage src={mostRecentReply.avatar_url || ''} className="object-cover" />
                    <AvatarFallback className="bg-muted text-xs">
                      {mostRecentReply.username[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <Collapsible open={isExpanded} onOpenChange={toggleReplies} className="flex-1">
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-3 text-primary hover:text-primary/80 font-medium"
                    >
                      {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                      <ChevronRight className={`h-4 w-4 ml-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-0 mt-2">
                    <div className="-ml-8 sm:-ml-9">
                      {replies.map(reply => renderPost(reply, depth + 1))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!topicData) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-32 pb-20 bg-background">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-rockwell text-4xl font-semibold text-primary mb-6">
              Category Not Found
            </h1>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <SEO
        title={`${topicData.title} Community Forum | Freeman & Crown Law`}
        description={`Discuss ${topicData.title} with the community`}
        image="/lovable-uploads/8c630ffa-2bac-4996-be73-277885cc03f3.png"
        url={window.location.origin + `/category/${categoryId}/forum`}
        type="website"
      />
      
      {/* Scrollable main content */}
      <main className="flex-1 overflow-y-auto pt-32 pb-4 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <Link
            to={`/category/${categoryId}`}
            className="inline-flex items-center text-primary hover:text-primary/80 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to {topicData.title}
          </Link>

          <div className="text-center mb-12">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              {topicData.title} Community Forum
            </h1>
            <p className="text-xl text-muted-foreground">
              Share your insights and discuss with the community
            </p>
          </div>

          {/* Posts List */}
          <div className="space-y-0 mb-4">
            <h2 className="text-lg font-semibold mb-4">
              Posts ({posts.length})
            </h2>
            {fetchingPosts ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : posts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">
                No posts yet. Be the first to post!
              </p>
            ) : (
              posts.map(post => renderPost(post))
            )}
          </div>
        </div>
      </main>

      {/* Sticky bottom input */}
      <footer className="border-t-2 border-gold/30 bg-background sticky bottom-0 pb-safe shadow-lg z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          {user ? (
            <div>
              <MentionTextarea
                placeholder="What's on your mind? (type @ to mention someone)"
                value={newPost}
                onChange={setNewPost}
                className="min-h-[60px] mb-2 text-sm border-2 border-gold shadow-[0_0_15px_rgba(212,175,55,0.3)] focus-within:border-gold focus-within:shadow-[0_0_20px_rgba(212,175,55,0.5)]"
              />
              {newPost.trim() && (
                <div className="flex justify-end">
                  <Button
                    onClick={(e) => handleSubmitPost(e)}
                    disabled={loading}
                    size="sm"
                    className="h-8 px-4 rounded-full"
                  >
                    Post
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="py-4 text-center">
              <p className="text-muted-foreground mb-3 text-sm">
                Please sign in to create a post
              </p>
              <Button onClick={() => window.location.href = '/auth'} size="sm">
                Sign In
              </Button>
            </div>
          )}
        </div>
      </footer>
      
      <Footer />
    </div>
  );
};

export default CategoryForumPage;
